import { ProgramScopedContentRow } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';
import { sortContentRowsByOrder } from './scheduleContentMatcher';

/** v5: contentVersion(head) 비교용 메타 */
const CACHE_VERSION = 'v5';

type Payload = {
  rows: ProgramScopedContentRow[];
  savedAt: number;
  contentVersion?: string;
};

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.annualProgressScopedTerm.${CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function normalizeRows(raw: unknown): ProgramScopedContentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is ProgramScopedContentRow => !!row && typeof row === 'object');
}

export function loadAnnualProgressScopedTermCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: ProgramScopedContentRow[]; contentVersion: string | null } {
  if (!workspaceId || !gradeId || typeof window === 'undefined') {
    return { found: false, rows: [], contentVersion: null };
  }
  try {
    const raw = deviceCacheStorage.getItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
    if (!raw) return { found: false, rows: [], contentVersion: null };
    const parsed = JSON.parse(raw) as Payload | ProgramScopedContentRow[];
    if (Array.isArray(parsed)) {
      const rows = normalizeRows(parsed);
      return { found: true, rows, contentVersion: buildLearningContentScopeVersion(rows) };
    }
    if (!parsed || typeof parsed !== 'object') return { found: false, rows: [], contentVersion: null };
    const rows = normalizeRows((parsed as Payload).rows);
    const contentVersion =
      typeof (parsed as Payload).contentVersion === 'string' && (parsed as Payload).contentVersion!.trim()
        ? (parsed as Payload).contentVersion!.trim()
        : buildLearningContentScopeVersion(rows);
    return { found: true, rows, contentVersion };
  } catch {
    return { found: false, rows: [], contentVersion: null };
  }
}

export function saveAnnualProgressScopedTermCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramScopedContentRow[],
  options?: { contentVersion?: string }
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows);
    const payload: Payload = { rows, savedAt: Date.now(), contentVersion };
    deviceCacheStorage.setItem(storageKey(workspaceId, academicYear, termNumber, gradeId), JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function clearAnnualProgressScopedTermCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
  } catch {
    /* ignore */
  }
}

/**
 * 학년·학기 스코프 행 목록 정리(프로그램 표와 같은 규칙):
 * 같은 id 는 최근 수정본 하나만 남기고, order_index → created_at → id 순으로 정렬한다.
 */
export function normalizeAnnualProgressScopedTermRows(rows: readonly ProgramScopedContentRow[]): ProgramScopedContentRow[] {
  if (!rows.length) return [];
  const byId = new Map<string, ProgramScopedContentRow>();
  const withoutId: ProgramScopedContentRow[] = [];
  rows.forEach((row) => {
    const id = (row.id ?? '').trim();
    if (!id) {
      withoutId.push(row);
      return;
    }
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, row);
      return;
    }
    const prevUpdated = String(prev.updated_at ?? prev.created_at ?? '');
    const nextUpdated = String(row.updated_at ?? row.created_at ?? '');
    if (nextUpdated >= prevUpdated) byId.set(id, row);
  });
  return sortContentRowsByOrder([...withoutId, ...Array.from(byId.values())]);
}

export function mergeAnnualProgressScopedTermCacheRows(
  existingRows: ProgramScopedContentRow[],
  updatedRows: ProgramScopedContentRow[]
): ProgramScopedContentRow[] {
  if (updatedRows.length === 0) return existingRows;
  const updatedById = new Map<string, ProgramScopedContentRow>();
  updatedRows.forEach((row) => {
    if (row.id) updatedById.set(row.id, row);
  });
  const merged = existingRows.map((row) => updatedById.get(row.id) ?? row);
  const existingIdSet = new Set(existingRows.map((row) => row.id));
  updatedRows.forEach((row) => {
    if (!existingIdSet.has(row.id)) merged.push(row);
  });
  /** 새 행이 끝에 붙은 채로 두면 "i번째 일정 = i번째 행" 짝이 프로그램 표와 어긋난다 */
  return normalizeAnnualProgressScopedTermRows(merged);
}

/**
 * 한 스코프(편제·프로젝트)의 행을 저장 결과(그 스코프 전체)로 통째 교체한다.
 * 저장으로 지워졌거나 id가 바뀐 옛 행이 연간진도 캐시에 남아 옛 학습내용이 보이는 것을 막는다.
 */
export function replaceScopeRowsInAnnualProgressScopedTermRows(
  existingRows: readonly ProgramScopedContentRow[],
  scope: { termNumber: 1 | 2; scopeType: string; scopeKey: string },
  scopeRows: readonly ProgramScopedContentRow[]
): ProgramScopedContentRow[] {
  const others = existingRows.filter((row) => {
    const tn = row.term_number;
    if ((tn === 1 || tn === 2) && tn !== scope.termNumber) return false;
    return !(row.scope_type === scope.scopeType && row.scope_key === scope.scopeKey);
  });
  return normalizeAnnualProgressScopedTermRows([...others, ...scopeRows]);
}

/** 삭제된 행 id 를 학년·학기 스코프 행 목록에서 뺀다 */
export function removeIdsFromAnnualProgressScopedTermRows(
  existingRows: readonly ProgramScopedContentRow[],
  ids: ReadonlySet<string>
): ProgramScopedContentRow[] {
  if (!ids.size) return [...existingRows];
  return existingRows.filter((row) => !ids.has(row.id));
}
